import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import api from '../../services/useApi';

interface NfcomViewInfoProps {
  open: boolean;
  onClose: () => void;
  notaId: number | null;
}

const NfcomViewInfo: React.FC<NfcomViewInfoProps> = ({
  open,
  onClose,
  notaId
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [nota, setNota] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && notaId) {
      carregarNota();
    } else {
      setNota(null);
      setError(null);
    }
  }, [open, notaId]);

  const carregarNota = async () => {
    if (!notaId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/notas-nfcom/${notaId}`);
      const body = response.data as { success?: boolean; data?: any; error?: string };
      // Backend (obterNota) retorna { success: true, data: nota }; lista já usa response.data.data na página.
      if (body?.success === true && body.data !== undefined) {
        setNota(body.data);
      } else if (body?.error) {
        setError(String(body.error));
        setNota(null);
      } else {
        setNota(body?.data ?? body);
      }
    } catch (err: any) {
      console.error('Erro ao carregar nota:', err);
      setError(err?.response?.data?.message || err?.message || 'Erro ao carregar informações da nota');
    } finally {
      setLoading(false);
    }
  };

  const formatarJson = (obj: any): string => {
    if (!obj) return '-';
    
    try {
      // Se já for uma string, tenta fazer parse primeiro
      if (typeof obj === 'string') {
        try {
          const parsed = JSON.parse(obj);
          return JSON.stringify(parsed, null, 2);
        } catch {
          // Se não conseguir fazer parse, retorna a string original
          return obj;
        }
      }
      
      // Se for um objeto, formata diretamente
      return JSON.stringify(obj, null, 2);
    } catch (error) {
      console.error('Erro ao formatar JSON:', error);
      return String(obj);
    }
  };

  const copiarJson = async () => {
    if (!nota?.gateway_response) return;
    
    try {
      const jsonFormatado = formatarJson(nota.gateway_response);
      await navigator.clipboard.writeText(jsonFormatado);
      // Poderia adicionar um toast aqui se necessário
    } catch (error) {
      console.error('Erro ao copiar JSON:', error);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const urlConsultarOnline = nota?.chave_acesso
    ? `https://dfe-portal.svrs.rs.gov.br/NFCom/QRCode?chNFCom=${nota.chave_acesso}&tpAmb=1`
    : null;

  const handleConsultarOnline = () => {
    if (urlConsultarOnline) window.open(urlConsultarOnline, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <InfoIcon color="primary" />
            <Typography variant="h6">
              Informações da NFCom
            </Typography>
            {nota && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                #{nota.id}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : nota ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Motivo do Status */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>
                Motivo do Status
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  bgcolor: 'background.paper',
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                {nota.motivo_status || '-'}
              </Typography>
            </Paper>

            {/* Gateway Response */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  Resposta do Gateway
                </Typography>
                {nota.gateway_response && (
                  <Tooltip title="Copiar JSON">
                    <IconButton
                      size="small"
                      onClick={copiarJson}
                      sx={{ color: 'text.secondary' }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
                  color: theme.palette.mode === 'dark' ? '#d4d4d4' : '#212121',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  maxHeight: '400px',
                  overflow: 'auto',
                  position: 'relative'
                }}
              >
                <Typography
                  component="pre"
                  variant="body2"
                  sx={{
                    fontFamily: '"Fira Code", "Consolas", "Monaco", "Courier New", monospace',
                    fontSize: '0.813rem',
                    lineHeight: 1.6,
                    whiteSpace: 'pre',
                    wordBreak: 'normal',
                    margin: 0,
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': {
                      height: '8px',
                      width: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                      background: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: theme.palette.mode === 'dark' ? '#424242' : '#bdbdbd',
                      borderRadius: '4px',
                      '&:hover': {
                        background: theme.palette.mode === 'dark' ? '#4e4e4e' : '#9e9e9e'
                      }
                    }
                  }}
                >
                  {formatarJson(nota.gateway_response)}
                </Typography>
              </Box>
            </Paper>
          </Box>
        ) : (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Nenhuma informação disponível
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {urlConsultarOnline && (
          <Button
            onClick={handleConsultarOnline}
            variant="contained"
            startIcon={<OpenInNewIcon />}
            sx={{ mr: 'auto' }}
          >
            Consultar Online
          </Button>
        )}
        <Button onClick={handleClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NfcomViewInfo;

